import { DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import { MAX_DOWNLOAD_LIMIT } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  bulkBatchDownload,
  bulkPayablesListDownload
} from "../../../utils/bulkDownloadReports";
import { reportsDownload } from "../../../utils/utils";
import BatchListPage from "../../components/BatchListPage";
import CancelBatchDialog from "../../components/CancelBatchDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import RejectBatchDialog from "../../components/RejectBatchDialog";
import {
  useBatchStatusBulkApprove,
  useBatchStatusBulkCancel,
  useBatchStatusReject
} from "../../mutation";
import {
  useActiveBatchDetailsReportQuery,
  useBatchDetailsQuery,
  useBatchesQuery,
  usePayablesListQuery
} from "../../queries";
import { batchListUrl } from "../../urls";
import { getYear } from "../../utils/handlers";

function BatchList({ params }) {
  const intl = useIntl();
  const paginate = usePaginator();
  const navigate = useNavigator();
  const notify = useNotifier();
  const { user } = useUser();

  const [buttonState, setButtonState] = useState<ConfirmButtonTransitionState>(
    "default"
  );

  const [openModal, closeModal] = createDialogActionHandlers(
    navigate,
    batchListUrl,
    params
  );

  const { isSelected, listElements, toggle, toggleAll, reset } = useBulkActions(
    params.ids
  );

  const { updateListSettings, settings } = useListSettings(
    ListViews.PAYOUT_DETAILS_LIST
  );

  const paginationState = createPaginationState(settings.rowNumber, params);

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: {
        search: null
      }
    }),
    [params]
  );

  const { data: batchDetails } = useBatchDetailsQuery({
    displayLoader: true,
    skip: listElements.length !== 1,
    variables: {
      id: listElements[0]
    }
  });

  const { data, refetch } = useBatchesQuery({
    displayLoader: true,
    skip: false,
    variables: previewVariables
  });

  const batches = data?.batches?.edges.map(a => a?.node);
  const batchDetail = batchDetails?.batchDetails?.data?.map(a => a);
  const selectedBatch = batches?.find(b => b.id === listElements?.[0]);
  const batchIdFormat = `${selectedBatch?.mall_code}_${getYear(
    selectedBatch?.generated_at
  )}_${atob(listElements?.[0] || "")}`;

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.batches.pageInfo),
    paginationState,
    params
  );

  useEffect(() => {
    if (listElements.length > MAX_DOWNLOAD_LIMIT) {
      notify({
        status: "error",
        text: "You’ve reached the maximum number of batches to be downloaded"
      });
      // removes last added element
      listElements.pop();
    } else {
      refetch();
    }
  }, [settings.rowNumber, listElements]);

  const [batchStatusBulkApprove] = useBatchStatusBulkApprove({
    onCompleted: event => {
      const { batchErrors, count } = event?.batchStatusBulkApprove;

      if (batchErrors?.length !== 0) {
        notify({
          status: "error",
          text: batchErrors[0].message
        });
      } else {
        if (count === 1) {
          notify({
            status: "success",
            text: `${batchIdFormat} Active Batch has been approved`
          });
        } else {
          notify({
            status: "success",
            text: `${count} Batch IDs have been approved`
          });
        }

        setButtonState("success");
        reset();
        closeModal();
        refetch();
      }
    }
  });

  const [BatchStatusBulkCancel] = useBatchStatusBulkCancel({
    onCompleted: event => {
      const { batchStatusBulkCancel } = event;
      const { batchErrors, count } = batchStatusBulkCancel;

      setButtonState("default");

      if (batchErrors.length !== 0) {
        notify({
          status: "error",
          text: batchErrors[0].message
        });
      } else {
        if (count === 1) {
          notify({
            status: "success",
            text: `You have successfully cancelled Batch ID ${
              batchDetail[0].mall_code
            }_${getYear(batchDetail[0].date_generated)}_${atob(
              listElements[0]
            )}`
          });
          reset();
          closeModal();
          refetch();
        } else {
          notify({
            status: "success",
            text: `You've successfully cancelled ${count} batch IDs`
          });
          reset();
          closeModal();
          refetch();
        }
      }
    }
  });

  const [batchStatusReject] = useBatchStatusReject({
    onCompleted: data => {
      const { batchStatusReject } = data;
      if (batchStatusReject.batchErrors.length === 0) {
        notify({
          status: "success",
          text: `Batch has been rejected and returned to Open Orders.`
        });
        refetch();
      } else {
        notify({
          status: "error",
          text: "The server encountered an error processing the request"
        });
      }
    }
  });

  const { data: activeBatchDetailsReport } = useActiveBatchDetailsReportQuery({
    displayLoader: true,
    skip: listElements.length === 0,
    variables: {
      ids: listElements
    }
  });

  const arr = activeBatchDetailsReport?.activeBatchDetailsReport.edges.map(
    a => a.node
  );
  const groupedArr = Object.keys(_.groupBy(arr, "id"));

  const { data: payablesList } = usePayablesListQuery({
    displayLoader: true,
    skip:
      listElements.length === 0 ||
      user.userType.code !== SmopUserTypeCode.FINANCE_MANAGER,
    variables: {
      ids: listElements
    }
  });

  const handleDownload = () => {
    const isFNManager = user.userType.code === SmopUserTypeCode.FINANCE_MANAGER;

    bulkBatchDownload(
      arr,
      "id",
      isFNManager
        ? reportsDownload.fnManagerFormat.fields
        : reportsDownload.activeBatchPayouts.fields,
      () => {
        notify({
          status: "success",
          text: "Successfully downloaded the report"
        });
        closeModal();
      }
    );
  };

  const payablesListReport = payablesList?.payablesList.edges.map(a => a.node);
  const groupedPayablesList = Object.keys(
    _.groupBy(payablesListReport, "batch_id")
  );

  const handlePayablesList = () => {
    const data = JSON.parse(JSON.stringify(payablesListReport));
    bulkPayablesListDownload(
      data,
      "batch_id",
      reportsDownload.payablesListPayouts.fields,
      () => {
        notify({
          status: "success",
          text: "Successfully downloaded the report"
        });
        closeModal();
      }
    );
  };

  return (
    <div>
      <BatchListPage
        batches={batches}
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={(items, selected) =>
          toggleAll(items, selected, MAX_DOWNLOAD_LIMIT)
        }
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        settings={settings}
        listElements={listElements}
        onCancelClick={() => openModal("cancel")}
        onBatchApprove={ids => openModal("approve", { ids })}
        onBatchReject={(ids: any[]) => {
          if (ids.length > 1) {
            openModal("rejectNote");
          } else {
            openModal("reject", { ids });
          }
        }}
        openModal={openModal}
      />

      <ConfirmDialog
        buttonState={buttonState}
        closeButtonLabel="back"
        content={
          <FormattedMessage defaultMessage="Are you sure you wanted to Approve?" />
        }
        contentAlignment="center"
        fullWidthButtons={true}
        onClose={closeModal}
        onConfirm={() => {
          setButtonState("loading");
          batchStatusBulkApprove({
            variables: {
              ids: listElements
            }
          });
        }}
        open={params.action === "approve"}
      />

      <CancelBatchDialog
        open={params.action === "cancel"}
        onClose={closeModal}
        listElements={listElements}
        loading={buttonState === "loading"}
        onConfirm={() => {
          BatchStatusBulkCancel({
            variables: {
              ids: listElements
            }
          });
          setButtonState("loading");
        }}
        cancelButtonLabel="Back"
        confirmButtonLabel="Yes"
      />
      <RejectBatchDialog
        open={params.action === "reject"}
        onClose={closeModal}
        onClickReject={remarks => {
          batchStatusReject({
            variables: {
              id: params.ids[0],
              remarks
            }
          });
          closeModal();
        }}
      />
      <ActionDialog
        open={params.action === "rejectNote"}
        confirmButtonState="default"
        hideCancelButton={true}
        onClose={closeModal}
        onConfirm={closeModal}
      >
        <p style={{ textAlign: "center" }}>
          You are required to input the Reason for Rejection for each Batch ID.
          Please select one Batch ID at a time.
        </p>
      </ActionDialog>

      <ConfirmDialog
        buttonState={buttonState}
        content={
          <FormattedMessage
            defaultMessage="You are about to download Active Batch with {total} row{s}. Do you want to continue?"
            values={{
              s: groupedArr?.length > 1 ? "s" : "",
              total: groupedArr?.length
            }}
          />
        }
        onClose={() => {
          closeModal();
          setButtonState("default");
        }}
        onConfirm={handleDownload}
        open={params.action === "download-active-batch"}
        title="Download Active Batch"
      />

      <ActionDialog
        open={params.action === "FnManagerdowloadActiveDetails"}
        onClose={closeModal}
        confirmButtonState={buttonState}
        onConfirm={handleDownload}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Batch",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download batch with {total} row{s}. Do you want to continue?"
            values={{
              s: groupedArr?.length > 1 ? "s" : "",
              total: groupedArr?.length
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <ActionDialog
        open={params.action === "FnManagerGeneratePayableList"}
        onClose={closeModal}
        confirmButtonState={buttonState}
        onConfirm={handlePayablesList}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Generate Payable List",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download Payable List with {total} row{s}. Do you want to continue?"
            values={{
              s: groupedPayablesList?.length > 1 ? "s" : "",
              total: groupedPayablesList?.length
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </div>
  );
}

export default BatchList;
